import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ChipInput from '../../components/shared/ChipInput';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import {
  Typography,
  Divider,
  Tab,
  Tabs,
  Grid,
  Card,
  CardContent,
  Checkbox,
  InputLabel,
  FormHelperText,
  FormControl,
  FormControlLabel,
  Select,
  Box,
  Button,
  TextField,
  Container,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { mongoObjectId } from '../../utils/mongoObjectId';
import RecipeIngredient from './RecipeIngredient';
import RecipeInstruction from './RecipeInstruction';
import Page from '../Page';
import Header from './Header';
import Loading from '../../components/shared/Loading';
import LabelledOutline from '../../components/shared/LabelledOutline';
import MediaUpload from '../../components/shared/DirectUpload';
import MealPreview from './MealPreview';
import { useMutation, useQuery } from '@apollo/client/react';
import { GET_MEAL, UPDATE_MEAL } from '../../graphql/nutrition';
import ServerError from '../error/ServerError';
import config from '../../config';

const defaultIngredient = {
  name: '',
  quantity: 1,
  quantityUnit: 'CUP',
  macros: {
    fatGrams: 0,
    carbGrams: 0,
    proteinGrams: 0,
    calories: 0,
  },
};
const defaultInstruction = {
  text: '',
};

const tabs = [
  { value: 'details', label: 'Details' },
  { value: 'preview', label: 'Preview' },
];

const MealEditor = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('details');
  const [meal, setMeal] = useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const id = params.id;
  const { error, loading, data } = useQuery(GET_MEAL, { variables: { id } });
  const [updateMeal] = useMutation(UPDATE_MEAL, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 2000,
      });
    },
  });

  useEffect(() => {
    setMeal(data?.meal);
  }, [data]);

  const handleSave = () => {
    if (!meal) {
      return;
    }
    enqueueSnackbar('Saving...', { autoHideDuration: 2000 });

    const input = {
      title: meal.title,
      tags: meal.tags,
      allergies: meal.allergies,
      desc: meal.desc,
      status: meal.status,
      prepTimeSeconds: parseInt(meal.prepTimeSeconds),
      cookTimeSeconds: parseInt(meal.cookTimeSeconds),
      recipeYield: parseInt(meal.recipeYield || 1),
      isUsingIngredientMacros: meal.isUsingIngredientMacros || false,

      macros: {
        fatGrams: parseFloat(meal.macros?.fatGrams || 0),
        proteinGrams: parseFloat(meal.macros?.proteinGrams || 0),
        carbGrams: parseFloat(meal.macros?.carbGrams || 0),
        calories: parseFloat(meal.macros?.calories || 0),
      },

      coverImages: meal.coverImages || [],
      // forced to cast quantity to int
      recipeIngredients:
        meal.recipeIngredients.map((i) => ({
          name: i.name,
          quantity: parseFloat(i.quantity),
          quantityUnit: i.quantityUnit,
          macros: {
            fatGrams: parseFloat(i.macros?.fatGrams || 0),
            proteinGrams: parseFloat(i.macros?.proteinGrams || 0),
            carbGrams: parseFloat(i.macros?.carbGrams || 0),
            calories: parseFloat(i.macros?.calories || 0),
          },
        })) || [],
      recipeInstructions:
        meal.recipeInstructions.map((i) => ({ text: i.text })) || [],
    };

    updateMeal({
      variables: {
        id: meal._id,
        input,
      },
    });
  };

  const handleChange = (event) => {
    let newMeal = _.cloneDeep(meal);
    newMeal = _.set(newMeal, event.target.name, event.target.value);
    setMeal(newMeal);
  };

  const handleAddIngredient = (ingredient) => {
    let newMeal = { ...meal };

    if (newMeal.recipeIngredients?.length > 0) {
      newMeal.recipeIngredients = [...newMeal.recipeIngredients, ingredient];
    } else {
      newMeal.recipeIngredients = [ingredient];
    }

    setMeal(newMeal);
  };

  const handleUpdateIngredient = (recipeIngredient) => {
    let newMeal = _.cloneDeep(meal);

    newMeal.recipeIngredients = newMeal.recipeIngredients.map((i) => {
      if (i._id === recipeIngredient._id) {
        return recipeIngredient;
      }
      return i;
    });

    setMeal(newMeal);
  };

  const handleRemoveIngredient = (recipeIngredient) => {
    let newMeal = { ...meal };
    newMeal.recipeIngredients = newMeal.recipeIngredients.filter(
      (f) => f._id !== recipeIngredient._id
    );
    setMeal(newMeal);
  };

  const handleAddInstruction = (recipeInstruction) => {
    let newMeal = { ...meal };
    if (newMeal.recipeInstructions?.length > 0) {
      newMeal.recipeInstructions = [
        ...newMeal.recipeInstructions,
        recipeInstruction,
      ];
    } else {
      newMeal.recipeInstructions = [recipeInstruction];
    }

    setMeal(newMeal);
  };

  const handleUpdateInstruction = (recipeInstruction) => {
    let newMeal = _.cloneDeep(meal);

    newMeal.recipeInstructions = newMeal.recipeInstructions.map((i) => {
      if (i._id === recipeInstruction._id) {
        return recipeInstruction;
      }
      return i;
    });

    setMeal(newMeal);
  };

  const handleRemoveInstruction = (recipeInstruction) => {
    let newMeal = { ...meal };
    newMeal.recipeInstructions = newMeal.recipeInstructions.filter(
      (f) => f._id !== recipeInstruction._id
    );
    setMeal(newMeal);
  };

  const handleUploadError = () => {
    enqueueSnackbar(`Media upload failed`, {
      variant: 'error',
      autoHideDuration: 2000,
    });
  };
  const handleUploadSuccess = (uploadedResponse) => {
    setMeal({ ...meal, coverImages: [uploadedResponse.image] });
  };
  const handleUploadRemove = () => {
    setMeal({ ...meal, coverImages: [] });
  };

  const handleAddTag = (tag) => {
    setMeal({ ...meal, tags: [...meal.tags, tag] });
  };

  const handleDeleteTag = (chip) => {
    const newTags = (meal.tags || []).filter((item) => item !== chip);
    setMeal({ ...meal, tags: newTags });
  };

  const handleAddAllergy = (allergy) => {
    setMeal({ ...meal, allergies: [...(meal.allergies || []), allergy] });
  };

  const handleDeleteAllergy = (chip) => {
    const newAllergies = (meal.allergies || []).filter((item) => item !== chip);
    setMeal({ ...meal, allergies: newAllergies });
  };

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
    navigate(`/trainer/meals/${meal._id}/${value}`);
  };

  const handleMacrosSourceChange = (e) => {
    const target = e.target;

    const changes = { ...meal };
    changes[e.target.name] = e.target.value;

    let isUsingIngredientMacros = meal.isUsingIngredientMacros;
    if (
      target.name === 'isUsingIngredientMacros' &&
      target.type === 'checkbox'
    ) {
      isUsingIngredientMacros = target.checked;
      changes.isUsingIngredientMacros = isUsingIngredientMacros;

      if (isUsingIngredientMacros) {
        changes.macros = computeMacrosFromIngredients(meal.recipeIngredients);
      }
    }

    setMeal(changes);
  };

  function computeMacrosFromIngredients(recipeIngredients) {
    let fatGramsTotal = 0;
    let carbGramsTotal = 0;
    let proteinGramsTotal = 0;
    let caloriesTotal = 0;
    let newMacros = {};
    for (const ingredient of recipeIngredients) {
      fatGramsTotal += parseInt(ingredient.macros.fatGrams);
      carbGramsTotal += parseInt(ingredient.macros.carbGrams);
      proteinGramsTotal += parseInt(ingredient.macros.proteinGrams);
      caloriesTotal += parseInt(ingredient.macros.calories);
      newMacros = {
        fatGrams: parseFloat(fatGramsTotal),
        proteinGrams: parseFloat(proteinGramsTotal),
        carbGrams: parseFloat(carbGramsTotal),
        calories: parseFloat(caloriesTotal),
      };
    }

    return newMacros;
  }

  if (error) {
    return <ServerError message={error.message} />;
  }

  if (loading || !meal) {
    return <Loading />;
  }

  return (
    <Page title={`Meal Editor : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Box mb={3}>
          <Header item={meal} />
          <Box mt={3}>
            <Tabs
              onChange={handleTabsChange}
              scrollButtons="auto"
              value={currentTab}
              variant="scrollable"
              textColor="secondary"
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
            <Divider />
          </Box>
        </Box>

        {currentTab === 'details' && (
          <Card id="main">
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    onChange={handleChange}
                    value={meal.title}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    multiline
                    label="Description"
                    name="desc"
                    type="text"
                    rows={4}
                    maxLength={320}
                    onChange={handleChange}
                    value={meal.desc ?? ''}
                    variant="outlined"
                    placeholder="Describe this meal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <LabelledOutline id="cover-image" label="Cover Image">
                    <MediaUpload
                      images={meal.coverImages || []}
                      onError={handleUploadError}
                      onSuccess={handleUploadSuccess}
                      onRemove={handleUploadRemove}
                      maxFiles={1}
                    />{' '}
                  </LabelledOutline>
                  <FormHelperText>A cover image for the meal.</FormHelperText>
                </Grid>

                <Grid item xs={12} md={8}>
                  <LabelledOutline id="time" label="Recipe Time">
                    <Box
                      mt={2}
                      style={{
                        width: 500,
                        alignItems: 'center',
                        display: 'flex',
                      }}
                    >
                      <Box mb={2} mr={2}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel id="prepTimeSeconds">
                            Prep Time
                          </InputLabel>
                          <Select
                            labelId="prepTimeSeconds"
                            name="prepTimeSeconds"
                            value={meal.prepTimeSeconds || 300}
                            label="Prep Time"
                            onChange={handleChange}
                            native
                          >
                            <option value={300}>5 min</option>
                            <option value={600}>10 min</option>
                            <option value={900}>15 min</option>
                            <option value={1200}>20 min</option>
                            <option value={1800}>30 min</option>
                            <option value={2400}>45 min</option>
                            <option value={3600}>1 hr</option>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box mb={2}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel id="cookTimeSeconds">
                            Cook Time
                          </InputLabel>
                          <Select
                            labelId="cookTimeSeconds"
                            name="cookTimeSeconds"
                            value={meal.cookTimeSeconds || 300}
                            label="Cook Time"
                            onChange={handleChange}
                            native
                          >
                            <option value={0}>0 min</option>
                            <option value={300}>5 min</option>
                            <option value={600}>10 min</option>
                            <option value={900}>15 min</option>
                            <option value={1200}>20 min</option>
                            <option value={1800}>30 min</option>
                            <option value={2400}>45 min</option>
                            <option value={3600}>1 hr</option>
                          </Select>
                        </FormControl>
                      </Box>

                      <Box mb={2}>
                        <TextField
                          label="Servings"
                          name="recipeYield"
                          onChange={handleChange}
                          value={meal.recipeYield}
                          variant="outlined"
                          InputProps={{ inputProps: { min: 0, max: 10 } }}
                          InputLabelProps={{ shrink: true }}
                          type="number"
                          style={{ width: 120, marginLeft: 20 }}
                        />
                      </Box>
                    </Box>
                  </LabelledOutline>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Typography variant="h4" gutterBottom>
                    Meal Macros{' '}
                  </Typography>
                  <LabelledOutline id="meal-macros">
                    <div
                      style={{
                        marginBottom: 10,
                        marginTop: 10,
                        display: 'flex',
                        gap: 20,
                      }}
                    >
                      {[
                        { label: 'Fat Grams', attribute: 'fatGrams' },
                        { label: 'Carb Grams', attribute: 'carbGrams' },
                        { label: 'Protein Grams', attribute: 'proteinGrams' },
                        { label: 'Calories', attribute: 'calories' },
                      ].map(({ label, attribute }) => {
                        const value = meal.macros[attribute] || '';

                        return (
                          <div key={attribute}>
                            <TextField
                              fullWidth
                              placeholder="0"
                              label={label}
                              name={`macros.${attribute}`}
                              onChange={handleChange}
                              value={value}
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              type="number"
                              style={{ width: 120 }}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <FormControlLabel
                      label="Use ingredient macros"
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={!!meal.isUsingIngredientMacros}
                          onChange={handleMacrosSourceChange}
                          name="isUsingIngredientMacros"
                          color="primary"
                        />
                      }
                    />
                  </LabelledOutline>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Typography variant="h4" gutterBottom>
                    Recipe Ingredients{' '}
                  </Typography>
                  {meal.recipeIngredients.map((recipeIngredient) => {
                    return (
                      <RecipeIngredient
                        ingredient={recipeIngredient}
                        key={recipeIngredient._id}
                        onChange={handleUpdateIngredient}
                        onRemove={handleRemoveIngredient}
                      />
                    );
                  })}

                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => {
                        handleAddIngredient({
                          ...defaultIngredient,
                          _id: mongoObjectId(),
                        });
                      }}
                    >
                      Add Another Ingredient
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Typography variant="h4" gutterBottom>
                    Recipe Instructions
                  </Typography>

                  {meal.recipeInstructions.map((recipeInstruction) => {
                    return (
                      <RecipeInstruction
                        key={recipeInstruction._id}
                        instruction={recipeInstruction}
                        onChange={handleUpdateInstruction}
                        onRemove={handleRemoveInstruction}
                      />
                    );
                  })}

                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => {
                        handleAddInstruction({
                          ...defaultInstruction,
                          _id: mongoObjectId(),
                        });
                      }}
                    >
                      Add Another Instruction
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Typography variant="h4" gutterBottom>
                    Tags
                  </Typography>
                  <FormControl>
                    <ChipInput
                      label="Tags"
                      value={meal.tags ?? []}
                      onAdd={handleAddTag}
                      onDelete={handleDeleteTag}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                    <FormHelperText>
                      Add helpful tags for which users can find meals by, e.g.
                      "keto".
                    </FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormHelperText>
                      These are common tags you can add to help{' '}
                      {config.appDisplayName} better auto-generate a user's Meal
                      Plan.
                    </FormHelperText>
                    <Box
                      mt={1}
                      pb={2}
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 14,
                      }}
                    >
                      {[
                        'breakfast',
                        'lunch',
                        'dinner',
                        'snack',
                        'vegetarian',
                        'vegan',
                        'pescatarian',
                      ].map((tag) => {
                        return (
                          <Button
                            key={tag}
                            variant="outlined"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => {
                              handleAddTag(tag);
                            }}
                          >
                            {tag}
                          </Button>
                        );
                      })}
                    </Box>
                  </FormControl>

                  <Divider />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Typography variant="h4" gutterBottom>
                    Allergies
                  </Typography>
                  <FormControl>
                    <ChipInput
                      label="Allergies"
                      value={meal.allergies ?? []}
                      onAdd={handleAddAllergy}
                      onDelete={handleDeleteAllergy}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                    <FormHelperText>
                      Add any allergies from the below list. If a user
                      identifies an allergy, {config.appDisplayName} will not
                      use this recipe in their auto-generated plan. If there are
                      any additional allergy concerns, add them and we will
                      display them to the user.
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={8}>
                  <FormControl>
                    <Box
                      pb={2}
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 14,
                      }}
                    >
                      {[
                        'peanut',
                        'egg',
                        'milk',
                        'soy',
                        'wheat',
                        'shellfish',
                        'treenut',
                        'fish',
                      ].map((allergy) => {
                        return (
                          <Button
                            key={allergy}
                            variant="outlined"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => {
                              handleAddAllergy(allergy);
                            }}
                          >
                            {allergy}
                          </Button>
                        );
                      })}
                      ,
                    </Box>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={8}>
                  <FormControl variant="outlined">
                    <InputLabel htmlFor="status">Status</InputLabel>
                    <Select
                      native
                      value={meal.status}
                      onChange={handleChange}
                      label="Status"
                      inputProps={{
                        name: 'status',
                        id: 'status',
                      }}
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="FREE">Free</option>
                      <option value="PREMIUM">Premium</option>
                    </Select>
                    <FormHelperText>
                      Drafts are seen only by you while you create the meal.{' '}
                      <br /> Public are free to view. <br /> Premium are
                      available only to paid members.
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {currentTab === 'preview' && <MealPreview meal={meal} />}
      </Container>
    </Page>
  );
};

export default MealEditor;
